import { Application, Container, Graphics, Text } from 'pixi.js';
import { AudioEngine } from '../audio';
import { DROP_LABELS, DROP_SHORTCUTS, GAME } from '../sim/config';
import { countItems } from '../sim/inventory';
import { effectProgress, effectRadius, Simulation } from '../sim/simulation';
import { DROP_TYPES, type DropType, type SimulationEvent, type ZoneType } from '../sim/types';
import { loadSettings, saveSettings } from '../storage';

const COLORS = {
  cyan: 0x4df7ff,
  cyanSoft: 0x1fc1d8,
  magenta: 0xff4bd8,
  magentaSoft: 0xcf258e,
  amber: 0xffd166,
  red: 0xff4d68,
  violet: 0x9d6cff,
  forge: 0xff9d3d,
  shield: 0x73a7ff,
  decay: 0x6cff8d,
  background: 0x050812,
  panel: 0x0a1122,
  grid: 0x14243e,
};

const DROP_COLOR: Record<DropType, number> = {
  attractor: COLORS.decay,
  deflector: COLORS.red,
  speed: COLORS.amber,
  explosion: COLORS.forge,
  surge: 0xffffff,
};

const ZONE_COLOR: Record<ZoneType, number> = {
  forge: COLORS.forge,
  shield: COLORS.shield,
  decay: COLORS.decay,
};

interface VisualParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: number;
}

export class GameApp {
  private readonly pixi = new Application();
  private readonly simulation = new Simulation(Math.floor(Date.now() / 1000));
  private readonly world = new Container();
  private readonly background = new Graphics();
  private readonly zoneLayer = new Graphics();
  private readonly zoneLabels = new Container();
  private readonly effectLayer = new Graphics();
  private readonly effectLabels = new Container();
  private readonly unitLayer = new Graphics();
  private readonly particleLayer = new Graphics();
  private readonly coreLayer = new Graphics();
  private readonly previewLayer = new Graphics();
  private readonly settings = loadSettings();
  private readonly audio = new AudioEngine(this.settings.soundEnabled);
  private selected: DropType | null = null;
  private hover = { x: GAME.width / 2, y: GAME.height / 2, valid: true };
  private accumulator = 0;
  private lastUiSecond = -1;
  private previewSequence = '';
  private scale = 1;
  private offsetX = 0;
  private offsetY = 0;
  private lastScreenWidth = 0;
  private lastScreenHeight = 0;
  private particles: VisualParticle[] = [];

  private readonly canvasHost = this.required<HTMLElement>('#canvas-host');
  private readonly inventoryEl = this.required<HTMLElement>('#inventory');
  private readonly previewEl = this.required<HTMLElement>('#next-drops');
  private readonly deliveryTimerEl = this.required<HTMLElement>('#delivery-timer');
  private readonly deliveryConveyorEl = this.required<HTMLElement>('.delivery-conveyor');
  private readonly matchTimerEl = this.required<HTMLElement>('#match-timer');
  private readonly playerHealthEl = this.required<HTMLElement>('#player-health');
  private readonly aiHealthEl = this.required<HTMLElement>('#ai-health');
  private readonly playerHealthTextEl = this.required<HTMLElement>('#player-health-text');
  private readonly aiHealthTextEl = this.required<HTMLElement>('#ai-health-text');
  private readonly overlayEl = this.required<HTMLElement>('#game-overlay');
  private readonly overlayTitleEl = this.required<HTMLElement>('#overlay-title');
  private readonly overlayTextEl = this.required<HTMLElement>('#overlay-text');
  private readonly primaryButton = this.required<HTMLButtonElement>('#primary-action');
  private readonly pauseButton = this.required<HTMLButtonElement>('#pause-button');
  private readonly soundButton = this.required<HTMLButtonElement>('#sound-button');
  private readonly statusEl = this.required<HTMLElement>('#status-line');

  async init(): Promise<void> {
    await this.pixi.init({
      resizeTo: this.canvasHost,
      backgroundColor: COLORS.background,
      antialias: true,
      autoDensity: true,
      resolution: Math.min(window.devicePixelRatio || 1, 2),
      preference: 'webgl',
    });
    this.pixi.canvas.setAttribute('aria-label', 'Top-down arena with autonomous units');
    this.canvasHost.appendChild(this.pixi.canvas);
    this.world.addChild(this.background, this.zoneLayer, this.zoneLabels, this.effectLayer, this.effectLabels, this.unitLayer, this.particleLayer, this.coreLayer, this.previewLayer);
    this.pixi.stage.addChild(this.world);
    this.drawBackground();
    this.createZoneLabels();
    this.bindEvents();
    this.resizeWorld();
    this.renderUi();
    this.pixi.ticker.add((ticker) => this.frame(Math.min(ticker.deltaMS / 1000, 0.1)));
  }

  private frame(deltaSeconds: number): void {
    if (this.pixi.screen.width !== this.lastScreenWidth || this.pixi.screen.height !== this.lastScreenHeight) {
      this.resizeWorld();
    }
    if (this.simulation.phase === 'playing') {
      this.accumulator += deltaSeconds;
      let steps = 0;
      while (this.accumulator >= GAME.fixedDt && steps < GAME.maxCatchUpSteps) {
        this.simulation.step();
        this.accumulator -= GAME.fixedDt;
        steps += 1;
      }
      if (steps === GAME.maxCatchUpSteps) this.accumulator = 0;
    }

    const events = this.simulation.drainEvents();
    this.audio.play(events);
    this.spawnEventParticles(events);
    this.updateParticles(deltaSeconds);
    this.drawZones();
    this.drawEffects();
    this.drawUnits();
    this.drawParticles();
    this.drawCores();
    this.drawPlacementPreview();
    const second = Math.floor(this.simulation.time * 4);
    if (second !== this.lastUiSecond || events.length > 0) {
      this.lastUiSecond = second;
      this.renderUi();
    }
    if (this.simulation.phase === 'finished' && this.overlayEl.hidden) this.showResult();
  }

  private bindEvents(): void {
    window.addEventListener('resize', () => this.resizeWorld());
    window.addEventListener('keydown', (event) => this.onKey(event));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.simulation.phase === 'playing') this.togglePause(true);
    });
    window.addEventListener('blur', () => {
      if (this.simulation.phase === 'playing') this.togglePause(true);
    });
    this.pixi.canvas.addEventListener('pointermove', (event) => {
      this.hover = { ...this.toWorld(event), valid: true };
    });
    this.pixi.canvas.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      this.audio.unlock();
      if (!this.selected || this.simulation.phase !== 'playing') return;
      const point = this.toWorld(event);
      if (this.simulation.useDrop(0, this.selected, point.x, point.y)) {
        this.selected = null;
        this.renderUi();
      }
    });
    this.pixi.canvas.addEventListener('contextmenu', (event) => {
      event.preventDefault();
      this.selected = null;
      this.renderUi();
    });
    this.pixi.canvas.addEventListener('pointercancel', () => {
      this.selected = null;
      this.renderUi();
    });
    this.primaryButton.addEventListener('click', () => {
      this.audio.unlock();
      if (this.simulation.phase === 'ready') {
        this.simulation.start();
        this.overlayEl.hidden = true;
        this.renderUi();
      } else if (this.simulation.phase === 'paused') {
        this.simulation.start();
        this.overlayEl.hidden = true;
        this.renderUi();
      } else if (this.simulation.phase === 'finished') window.location.reload();
    });
    this.pauseButton.addEventListener('click', () => this.togglePause());
    this.soundButton.addEventListener('click', () => {
      this.settings.soundEnabled = !this.settings.soundEnabled;
      this.audio.enabled = this.settings.soundEnabled;
      if (this.settings.soundEnabled) this.audio.unlock();
      saveSettings(this.settings);
      this.renderUi();
    });
  }

  private onKey(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.selected) {
        this.selected = null;
        this.renderUi();
      } else this.togglePause();
      return;
    }
    if (event.key.toLowerCase() === 'p') return this.togglePause();
    const type = DROP_TYPES.find((candidate) => DROP_SHORTCUTS[candidate] === event.key);
    if (type) this.selectDrop(type);
  }

  private selectDrop(type: DropType): void {
    if (this.simulation.phase !== 'playing') return;
    if (!this.simulation.inventories[0].items.includes(type)) return;
    this.audio.unlock();
    if (type === 'surge') {
      this.simulation.useDrop(0, type, this.simulation.cores[0].x, this.simulation.cores[0].y);
      this.selected = null;
    } else this.selected = this.selected === type ? null : type;
    this.renderUi();
  }

  private togglePause(forcePause = false): void {
    if (this.simulation.phase === 'finished' || this.simulation.phase === 'ready') return;
    if (forcePause || this.simulation.phase === 'playing') {
      this.simulation.pause();
      this.selected = null;
      this.overlayTitleEl.textContent = 'SIGNAL PAUSED';
      this.overlayTextEl.textContent = 'The swarm is frozen. Resume when your next chain is ready.';
      this.primaryButton.textContent = 'Resume';
      this.overlayEl.hidden = false;
    } else {
      this.simulation.start();
      this.overlayEl.hidden = true;
    }
    this.renderUi();
  }

  private renderUi(): void {
    const remaining = Math.max(0, GAME.matchDuration - this.simulation.time);
    const time = this.simulation.suddenDeath ? GAME.matchDuration + GAME.suddenDeathDuration - this.simulation.time : remaining;
    this.matchTimerEl.textContent = this.simulation.suddenDeath ? `SUDDEN ${this.formatTime(time)}` : this.formatTime(time);
    const deliveryRemaining = Math.max(0, this.simulation.delivery.nextAt - this.simulation.time);
    const deliveryProgress = Math.max(0, Math.min(1, 1 - deliveryRemaining / GAME.deliveryInterval));
    this.deliveryTimerEl.textContent = `${deliveryRemaining.toFixed(1)}s`;
    const playerPercent = (this.simulation.cores[0].health / GAME.coreHealth) * 100;
    const aiPercent = (this.simulation.cores[1].health / GAME.coreHealth) * 100;
    this.playerHealthEl.style.width = `${playerPercent}%`;
    this.aiHealthEl.style.width = `${aiPercent}%`;
    this.playerHealthTextEl.textContent = `${this.simulation.cores[0].health}`;
    this.aiHealthTextEl.textContent = `${this.simulation.cores[1].health}`;
    this.pauseButton.textContent = this.simulation.phase === 'paused' ? '▶' : 'Ⅱ';
    this.soundButton.textContent = this.settings.soundEnabled ? 'SOUND ON' : 'SOUND OFF';

    const previewSequence = this.simulation.delivery.upcoming.join(',');
    if (previewSequence !== this.previewSequence || this.previewEl.childElementCount === 0) {
      this.previewSequence = previewSequence;
      this.previewEl.replaceChildren(
        ...this.simulation.delivery.upcoming.map((type, index) => {
          const item = document.createElement('span');
          item.className = `preview-chip drop-${type}`;
          item.title = DROP_LABELS[type];
          item.setAttribute('aria-label', `${DROP_LABELS[type]}, ${index === 0 ? 'next' : `number ${index + 1}`}`);
          item.innerHTML = `<i>${this.dropGlyph(type)}</i><b>${index === 0 ? 'NEXT' : `+${index}`}</b>`;
          return item;
        }),
      );
    }

    const chipWidth = parseFloat(getComputedStyle(this.deliveryConveyorEl).getPropertyValue('--drop-chip-size')) || 31;
    const step = Math.max(0, (this.deliveryConveyorEl.clientWidth - chipWidth) / Math.max(1, this.simulation.delivery.upcoming.length));
    for (const [index, chip] of Array.from(this.previewEl.children).entries()) {
      (chip as HTMLElement).style.left = `${Math.max(0, step * (index + 1 - deliveryProgress))}px`;
      const upcomingType = this.simulation.delivery.upcoming[index];
      if (upcomingType) (chip as HTMLElement).setAttribute('aria-label', `${DROP_LABELS[upcomingType]}, ${index === 0 ? 'next' : `number ${index + 1}`}`);
    }

    if (this.inventoryEl.childElementCount === 0) {
      this.inventoryEl.replaceChildren(
        ...DROP_TYPES.map((type) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `drop-button drop-${type}`;
          button.setAttribute('aria-label', `${DROP_LABELS[type]}, 0 available`);
          button.setAttribute('aria-pressed', 'false');
          button.innerHTML = `<kbd>${DROP_SHORTCUTS[type]}</kbd><span class="drop-glyph">${this.dropGlyph(type)}</span><span class="drop-name">${DROP_LABELS[type]}</span><span class="drop-quantity"><strong>0</strong><span class="charge-pips" aria-hidden="true">${Array.from({ length: GAME.inventoryCapacity }, () => '<i></i>').join('')}</span></span>`;
          button.addEventListener('click', () => this.selectDrop(type));
          return button;
        }),
      );
    }

    {
      const counts = countItems(this.simulation.inventories[0]);
      for (const [index, type] of DROP_TYPES.entries()) {
        const button = this.inventoryEl.children[index] as HTMLButtonElement;
        const count = counts.get(type) ?? 0;
        button.classList.toggle('selected', this.selected === type);
        button.disabled = count === 0 || this.simulation.phase !== 'playing';
        button.setAttribute('aria-label', `${DROP_LABELS[type]}, ${count} available`);
        button.setAttribute('aria-pressed', String(this.selected === type));
        const quantity = button.querySelector('.drop-quantity strong');
        if (quantity) quantity.textContent = `${count}`;
        button.querySelectorAll('.charge-pips i').forEach((pip, pipIndex) => {
          pip.classList.toggle('filled', pipIndex < count);
        });
      }
    }

    const inventory = this.simulation.inventories[0];
    if (inventory.overflow) {
      this.statusEl.textContent = `OVERFLOW: ${DROP_LABELS[inventory.overflow.type]} expires in ${Math.max(0, inventory.overflow.expiresAt - this.simulation.time).toFixed(1)}s`;
      this.statusEl.classList.add('danger');
    } else if (this.selected) {
      this.statusEl.textContent = `PLACE ${DROP_LABELS[this.selected].toUpperCase()} · Right click / Esc to cancel`;
      this.statusEl.classList.remove('danger');
    } else {
      this.statusEl.textContent = 'Select a drop';
      this.statusEl.classList.remove('danger');
    }
  }

  private drawBackground(): void {
    this.background.clear();
    this.background.rect(0, 0, GAME.width, GAME.height).fill({ color: COLORS.background });
    for (let x = 0; x <= GAME.width; x += 40) {
      this.background.moveTo(x, 0).lineTo(x, GAME.height).stroke({ color: COLORS.grid, alpha: x % 200 === 0 ? 0.42 : 0.17, width: 1 });
    }
    for (let y = 0; y <= GAME.height; y += 40) {
      this.background.moveTo(0, y).lineTo(GAME.width, y).stroke({ color: COLORS.grid, alpha: y % 200 === 0 ? 0.42 : 0.17, width: 1 });
    }
    this.background.rect(2, 2, GAME.width - 4, GAME.height - 4).stroke({ color: COLORS.cyan, alpha: 0.22, width: 2 });
    for (const obstacle of this.simulation.obstacles) {
      this.background.circle(obstacle.x, obstacle.y, obstacle.radius + 8).stroke({ color: COLORS.violet, alpha: 0.12, width: 8 });
      this.background.circle(obstacle.x, obstacle.y, obstacle.radius).fill({ color: 0x0b1327, alpha: 0.98 }).stroke({ color: 0x32466d, alpha: 0.8, width: 2 });
      this.background.circle(obstacle.x, obstacle.y, obstacle.radius * 0.62).stroke({ color: COLORS.cyan, alpha: 0.1, width: 1 });
    }
  }

  private createZoneLabels(): void {
    const names: Record<ZoneType, string> = { forge: 'FORGE', shield: 'SHIELD', decay: 'DECAY' };
    for (const zone of this.simulation.zones) {
      const label = new Text({
        text: names[zone.type],
        style: {
          fill: ZONE_COLOR[zone.type],
          fontFamily: 'ui-monospace, monospace',
          fontSize: 13,
          fontWeight: '700',
          letterSpacing: 2.4,
        },
      });
      label.anchor.set(0.5);
      label.position.set(zone.x, zone.y - zone.radius + 17);
      label.alpha = 0.9;
      this.zoneLabels.addChild(label);
    }
  }

  private drawZones(): void {
    this.zoneLayer.clear();
    const pulse = 0.5 + 0.5 * Math.sin(this.simulation.time * 3.2);
    for (const zone of this.simulation.zones) {
      const color = ZONE_COLOR[zone.type];
      if (zone.type === 'forge') {
        this.zoneLayer.circle(zone.x, zone.y, zone.radius).fill({ color, alpha: 0.075 }).stroke({ color, alpha: 0.92, width: 4 });
        this.zoneLayer.circle(zone.x, zone.y, zone.radius * (0.48 + pulse * 0.12)).stroke({ color, alpha: 0.35 + pulse * 0.25, width: 7 });
        for (let offset = -24; offset <= 24; offset += 24) {
          this.zoneLayer.moveTo(zone.x - 30 + offset, zone.y + 18).lineTo(zone.x + offset, zone.y - 18).lineTo(zone.x + 30 + offset, zone.y + 18).stroke({ color, alpha: 0.65, width: 4 });
        }
      } else if (zone.type === 'shield') {
        this.zoneLayer.circle(zone.x, zone.y, zone.radius).fill({ color, alpha: 0.065 }).stroke({ color, alpha: 0.92, width: 4 });
        this.zoneLayer.circle(zone.x, zone.y, zone.radius * 0.68).stroke({ color, alpha: 0.3 + pulse * 0.35, width: 8 });
        this.zoneLayer.circle(zone.x, zone.y, 22 + pulse * 8).fill({ color, alpha: 0.22 }).stroke({ color: 0xffffff, alpha: 0.72, width: 2 });
      } else {
        this.zoneLayer.circle(zone.x, zone.y, zone.radius).fill({ color, alpha: 0.11 }).stroke({ color, alpha: 0.85, width: 4 });
        this.zoneLayer.circle(zone.x, zone.y, zone.radius * (0.72 + pulse * 0.08)).stroke({ color, alpha: 0.18 + pulse * 0.28, width: 9 });
        for (let index = 0; index < 7; index += 1) {
          const angle = index * (TAU / 7) + this.simulation.time * 0.18;
          const distance = 35 + (index % 3) * 17;
          const radius = 3 + ((index + Math.floor(this.simulation.time * 2)) % 3) * 2;
          this.zoneLayer.circle(zone.x + Math.cos(angle) * distance, zone.y + Math.sin(angle) * distance, radius).fill({ color, alpha: 0.3 + pulse * 0.2 });
        }
      }
    }
  }

  private drawUnits(): void {
    this.unitLayer.clear();
    for (const unit of this.simulation.units) {
      const color = unit.team === 0 ? COLORS.cyan : COLORS.magenta;
      const soft = unit.team === 0 ? COLORS.cyanSoft : COLORS.magentaSoft;
      const healthRatio = Math.max(0.01, Math.min(1, unit.health / GAME.unitHealth));
      const radius = GAME.unitMinRadius + (GAME.unitRadius - GAME.unitMinRadius) * healthRatio;
      if (!this.settings.reducedMotion) this.unitLayer.circle(unit.x, unit.y, radius * 3.1).fill({ color, alpha: 0.09 + healthRatio * 0.07 });
      this.unitLayer.circle(unit.x, unit.y, radius).fill({ color, alpha: 0.95 });
      this.unitLayer.circle(unit.x, unit.y, Math.max(1.2, radius * 0.34)).fill({ color: 0xffffff, alpha: 0.9 });
      this.unitLayer.moveTo(unit.x, unit.y).lineTo(unit.x - unit.vx * 0.12, unit.y - unit.vy * 0.12).stroke({ color: soft, alpha: 0.85, width: 2.2 });
      if (unit.weapon) {
        this.unitLayer.circle(unit.x, unit.y, radius + 3).stroke({ color: COLORS.forge, alpha: 0.95, width: 2.2 });
        this.unitLayer.moveTo(unit.x - 5, unit.y - radius - 5).lineTo(unit.x, unit.y - radius - 10).lineTo(unit.x + 5, unit.y - radius - 5).stroke({ color: COLORS.forge, alpha: 1, width: 2 });
      }
      if (unit.shield) this.unitLayer.circle(unit.x, unit.y, radius + 6).stroke({ color: COLORS.shield, alpha: 0.95, width: 3 });
      if (unit.decayExposure > 0) {
        const warning = Math.min(1, unit.decayExposure / GAME.decayDuration);
        this.unitLayer.circle(unit.x, unit.y, radius + 9).stroke({ color: COLORS.decay, alpha: 0.35 + warning * 0.65, width: 1.5 + warning * 3 });
      }
    }
  }

  private drawEffects(): void {
    this.effectLayer.clear();
    const activeIds = new Set<number>();
    for (const effect of this.simulation.effects) {
      activeIds.add(effect.id);
      const radius = effectRadius(effect.type);
      const progress = effectProgress(effect, this.simulation.time);
      const color = DROP_COLOR[effect.type];
      if (effect.type === 'explosion') {
        const warning = Math.min(1, (this.simulation.time - effect.createdAt) / GAME.effect.explosion.warning);
        const visualRadius = radius * (0.2 + warning * 0.8);
        this.effectLayer.circle(effect.x, effect.y, visualRadius).fill({ color, alpha: effect.triggered ? 0.32 : 0.09 }).stroke({ color, alpha: 1, width: 5 });
        this.effectLayer.circle(effect.x, effect.y, Math.max(4, radius * (1 - warning))).stroke({ color: 0xffffff, alpha: 0.95, width: 3 });
      } else {
        this.effectLayer.circle(effect.x, effect.y, radius).fill({ color, alpha: 0.055 }).stroke({ color, alpha: 0.72 * (1 - progress * 0.45), width: 3 });
        this.effectLayer.moveTo(effect.x, effect.y - radius - 8);
        this.effectLayer.arc(effect.x, effect.y, radius + 8, -Math.PI / 2, -Math.PI / 2 + TAU * (1 - progress)).stroke({ color, alpha: 0.9, width: 3 });
        if (effect.type === 'deflector') {
          this.effectLayer.circle(effect.x, effect.y, radius * 0.72).stroke({ color, alpha: 0.2, width: 5 });
        } else if (effect.type === 'speed') {
          for (let i = -1; i <= 1; i += 1) {
            const y = effect.y + i * 26;
            this.effectLayer.moveTo(effect.x - radius * 0.45, y).lineTo(effect.x + radius * 0.42, y).stroke({ color, alpha: 0.32, width: 4 });
          }
        }
      }

      this.effectLayer.circle(effect.x, effect.y, 11).fill({ color: COLORS.background, alpha: 0.88 }).stroke({ color, alpha: 0.95, width: 2 });
      this.effectLayer.moveTo(effect.x - 7, effect.y).lineTo(effect.x + 7, effect.y).stroke({ color: 0xffffff, alpha: 0.95, width: 1.5 });
      this.effectLayer.moveTo(effect.x, effect.y - 7).lineTo(effect.x, effect.y + 7).stroke({ color: 0xffffff, alpha: 0.95, width: 1.5 });

      let durationLabel = this.effectLabels.children.find((child) => (child as Text & { effectId?: number }).effectId === effect.id) as Text | undefined;
      if (!durationLabel) {
        durationLabel = new Text({
          text: '',
          style: { fill: 0xffffff, fontFamily: 'ui-monospace, monospace', fontSize: 15, fontWeight: '700', stroke: { color: COLORS.background, width: 4 } },
        });
        durationLabel.anchor.set(0.5);
        (durationLabel as Text & { effectId?: number }).effectId = effect.id;
        this.effectLabels.addChild(durationLabel);
      }
      const durationText = `${Math.max(0, Math.ceil(effect.expiresAt - this.simulation.time))}s`;
      if (durationLabel.text !== durationText) durationLabel.text = durationText;
      const labelAbove = effect.y - radius - 20;
      const labelY = labelAbove >= 16 ? labelAbove : Math.min(GAME.height - 16, effect.y + radius + 20);
      durationLabel.position.set(Math.max(16, Math.min(GAME.width - 16, effect.x)), labelY);
    }
    for (const child of [...this.effectLabels.children]) {
      if (!activeIds.has((child as Text & { effectId?: number }).effectId ?? -1)) this.effectLabels.removeChild(child);
    }
  }

  private spawnEventParticles(events: readonly SimulationEvent[]): void {
    for (const event of events) {
      if (event.x === undefined || event.y === undefined) continue;
      if (event.type === 'elimination') {
        this.createBurst(event.x, event.y, 18, [COLORS.cyan, COLORS.magenta, 0xffffff], 45, 175, 0.55, 1.05);
      } else if (event.type === 'combat-hit') {
        this.createBurst(event.x, event.y, 8, [COLORS.cyan, COLORS.magenta, 0xffffff], 25, 90, 0.2, 0.45);
      } else if (event.type === 'explosion') {
        const amount = Math.min(120, 54 + (event.count ?? 0) * 3);
        this.createBurst(event.x, event.y, amount, [COLORS.red, COLORS.amber, 0xffffff], 100, 360, 0.65, 1.35);
      } else if (event.type === 'zone-charge' && event.zone) {
        this.createBurst(event.x, event.y, 14, [ZONE_COLOR[event.zone], 0xffffff], 25, 110, 0.35, 0.75);
      } else if (event.type === 'shield-break') {
        this.createBurst(event.x, event.y, 18, [COLORS.shield, 0xffffff], 45, 160, 0.35, 0.75);
      } else if (event.type === 'decay') {
        this.createBurst(event.x, event.y, 26, [COLORS.decay, 0xffffff], 35, 145, 0.45, 0.95);
      } else if (event.type === 'core-hit') {
        const color = event.team === 0 ? COLORS.cyan : COLORS.magenta;
        this.createBurst(event.x, event.y, 22, [color, 0xffffff], 55, 210, 0.5, 0.95);
      } else if (event.type === 'drop' && event.drop) {
        this.createBurst(event.x, event.y, 12, [DROP_COLOR[event.drop], 0xffffff], 30, 105, 0.35, 0.7);
      }
    }
  }

  private createBurst(
    x: number,
    y: number,
    amount: number,
    colors: readonly number[],
    minSpeed: number,
    maxSpeed: number,
    minLife: number,
    maxLife: number,
  ): void {
    const motionScale = this.settings.reducedMotion ? 0.3 : 1;
    for (let i = 0; i < amount; i += 1) {
      const angle = Math.random() * TAU;
      const speed = (minSpeed + Math.random() * (maxSpeed - minSpeed)) * motionScale;
      const life = minLife + Math.random() * (maxLife - minLife);
      this.particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        size: 2.4 + Math.random() * 4.8,
        color: colors[Math.floor(Math.random() * colors.length)] ?? 0xffffff,
      });
    }
    if (this.particles.length > 900) this.particles.splice(0, this.particles.length - 900);
  }

  private updateParticles(dt: number): void {
    for (const particle of this.particles) {
      particle.life -= dt;
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      const damping = Math.pow(0.045, dt);
      particle.vx *= damping;
      particle.vy *= damping;
    }
    this.particles = this.particles.filter((particle) => particle.life > 0);
  }

  private drawParticles(): void {
    this.particleLayer.clear();
    for (const particle of this.particles) {
      const alpha = Math.max(0, particle.life / particle.maxLife);
      this.particleLayer.circle(particle.x, particle.y, particle.size * 2.6).fill({ color: particle.color, alpha: alpha * 0.12 });
      this.particleLayer.circle(particle.x, particle.y, particle.size).fill({ color: particle.color, alpha: Math.min(1, alpha * 1.3) });
      this.particleLayer.moveTo(particle.x, particle.y).lineTo(particle.x - particle.vx * 0.035, particle.y - particle.vy * 0.035).stroke({ color: particle.color, alpha: alpha * 0.8, width: Math.max(1, particle.size * 0.45) });
    }
  }

  private drawCores(): void {
    this.coreLayer.clear();
    for (const core of this.simulation.cores) {
      const color = core.team === 0 ? COLORS.cyan : COLORS.magenta;
      const health = core.health / core.maxHealth;
      this.coreLayer.circle(core.x, core.y, GAME.coreRadius + 18).fill({ color, alpha: 0.09 }).stroke({ color, alpha: 0.3, width: 10 });
      this.coreLayer.circle(core.x, core.y, GAME.coreRadius).fill({ color: COLORS.panel, alpha: 1 }).stroke({ color, alpha: 1, width: 5 });
      this.coreLayer.moveTo(core.x, core.y - (GAME.coreRadius - 7));
      this.coreLayer.arc(core.x, core.y, GAME.coreRadius - 7, -Math.PI / 2, -Math.PI / 2 + TAU * health).stroke({ color, alpha: 1, width: 7 });
      this.coreLayer.circle(core.x, core.y, 13).fill({ color, alpha: 0.95 });
      this.coreLayer.circle(core.x, core.y, 5).fill({ color: 0xffffff, alpha: 0.95 });
    }
  }

  private drawPlacementPreview(): void {
    this.previewLayer.clear();
    if (!this.selected || this.selected === 'surge' || this.simulation.phase !== 'playing') return;
    const radius = effectRadius(this.selected);
    const color = DROP_COLOR[this.selected];
    this.previewLayer.circle(this.hover.x, this.hover.y, radius).fill({ color, alpha: 0.045 }).stroke({ color, alpha: 0.9, width: 2 });
    this.previewLayer.circle(this.hover.x, this.hover.y, 13).fill({ color: COLORS.background, alpha: 0.9 }).stroke({ color, alpha: 0.95, width: 2 });
    this.previewLayer.moveTo(this.hover.x - 8, this.hover.y).lineTo(this.hover.x + 8, this.hover.y).stroke({ color: 0xffffff, alpha: 0.95, width: 1.5 });
    this.previewLayer.moveTo(this.hover.x, this.hover.y - 8).lineTo(this.hover.x, this.hover.y + 8).stroke({ color: 0xffffff, alpha: 0.95, width: 1.5 });
  }

  private resizeWorld(): void {
    this.lastScreenWidth = this.pixi.screen.width;
    this.lastScreenHeight = this.pixi.screen.height;
    this.scale = Math.min(this.pixi.screen.width / GAME.width, this.pixi.screen.height / GAME.height);
    this.offsetX = (this.pixi.screen.width - GAME.width * this.scale) / 2;
    this.offsetY = (this.pixi.screen.height - GAME.height * this.scale) / 2;
    this.world.scale.set(this.scale);
    this.world.position.set(this.offsetX, this.offsetY);
  }

  private toWorld(event: PointerEvent): { x: number; y: number } {
    const rect = this.pixi.canvas.getBoundingClientRect();
    const screenX = ((event.clientX - rect.left) / rect.width) * this.pixi.screen.width;
    const screenY = ((event.clientY - rect.top) / rect.height) * this.pixi.screen.height;
    return {
      x: Math.max(0, Math.min(GAME.width, (screenX - this.offsetX) / this.scale)),
      y: Math.max(0, Math.min(GAME.height, (screenY - this.offsetY) / this.scale)),
    };
  }

  private showResult(): void {
    const won = this.simulation.winner === 0;
    this.overlayTitleEl.textContent = won ? 'SIGNAL DOMINANCE' : 'SIGNAL COLLAPSED';
    this.overlayTextEl.textContent = `CORE ${this.simulation.cores[0].health}:${this.simulation.cores[1].health} · DROPS ${Object.values(this.simulation.stats.dropsUsed[0]).reduce((a, b) => a + b, 0)} · HITS ${this.simulation.stats.coreHits[0]}`;
    this.primaryButton.textContent = 'New match';
    this.overlayEl.hidden = false;
  }

  private dropGlyph(type: DropType): string {
    return { attractor: '◎', deflector: '◈', speed: '»', explosion: '✹', surge: '⬡' }[type];
  }

  private formatTime(seconds: number): string {
    const safe = Math.max(0, Math.ceil(seconds));
    return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, '0')}`;
  }

  private required<T extends Element>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) throw new Error(`Missing required element: ${selector}`);
    return element;
  }
}

const TAU = Math.PI * 2;
